// COMP-REFERRAL-EDITOR — ارجاع داخلی (US-MED-060): مقصد (ارائه‌دهنده، تخصص یا شعبه)، دلیل، سؤال بالینی، فوریت و پیوست یادداشت مواجهه.
// ارائه‌دهنده مقصد برای مدت ارجاع عضو تیم درمان می‌شود (BR-MED-060). AP-CLN-04: ارجاع خارجی (US-MED-063) با پزشک/مرکز فهرست ارجاع‌دهندگان یا نام آزاد.
// AP-MED-03 (US-MED-074): تیک «ثبت در سامانه ارجاع ملی» — ارجاع داخلی ثبت می‌شود و سپس نمای ارجاع ملی همان مواجهه
// باز می‌شود. کدهای شکایت و تشخیص در یک جا (NationalReferralPanel) می‌مانند تا دو فرم موازی ساخته نشود.
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type MedReferralTargetKind, type MedReferralUrgency } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { FormDialog } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { COMP_REFERRAL_EDITOR, medKeys } from './common';
import { NationalReferralPanel } from './referralNational';

const KINDS: readonly MedReferralTargetKind[] = ['Provider', 'Specialty', 'Branch', 'External'];
const URGENCIES: readonly MedReferralUrgency[] = ['Routine', 'Urgent'];

export function ReferralEditor({ open, patientId, encounterId, onClose, onCreated }: { open: boolean; patientId: string; encounterId?: string | null; onClose: () => void; onCreated?: () => void }) {
  const { t } = useTranslation('med');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const [kind, setKind] = useState<MedReferralTargetKind>('Provider');
  const [providerId, setProviderId] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [branchId, setBranchId] = useState('');
  const [referrerId, setReferrerId] = useState('');
  const [externalName, setExternalName] = useState('');
  const [externalContact, setExternalContact] = useState('');
  const [reason, setReason] = useState('');
  const [question, setQuestion] = useState('');
  const [urgency, setUrgency] = useState<MedReferralUrgency>('Routine');
  const [done, setDone] = useState(false);
  // AC-MED-074.1: ثبت ملی فقط برای مواجهه باز و دارنده مجوز ارسال معنا دارد.
  const [alsoNational, setAlsoNational] = useState(false);
  const canNational = access.can('med.national_referral.submit') && Boolean(encounterId);
  const targets = useQuery({ queryKey: medKeys.targets(tenantId), enabled: open, queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/referral-targets')) });
  const specialties = [...new Set((targets.data?.items ?? []).flatMap((item) => item.specialties))].sort();
  const branches = [...new Set((targets.data?.items ?? []).flatMap((item) => item.branchIds))];
  const branchNames = activeTenant?.branches ?? [];
  const referrers = useQuery({ queryKey: medKeys.referrers(tenantId), enabled: open && kind === 'External', queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/referrers')) });
  const valid =
    reason.trim() !== '' &&
    (kind === 'Provider' ? providerId !== '' : kind === 'Specialty' ? specialty !== '' : kind === 'Branch' ? branchId !== '' : referrerId !== '' || externalName.trim() !== '');

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/referrals', {
          body: {
            patientId,
            encounterId: encounterId ?? null,
            targetKind: kind,
            toProviderId: kind === 'Provider' ? providerId : null,
            toSpecialty: kind === 'Specialty' ? specialty : null,
            toBranchId: kind === 'Branch' ? branchId : null,
            externalReferrerId: kind === 'External' && referrerId ? referrerId : null,
            externalName: kind === 'External' && !referrerId ? externalName.trim() || null : null,
            externalContact: kind === 'External' && !referrerId ? externalContact.trim() || null : null,
            reason: reason.trim(),
            clinicalQuestion: question.trim() || null,
            urgency,
            suggestedServiceIds: [],
            attachments: [],
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setDone(true);
      void queryClient.invalidateQueries({ queryKey: medKeys.patient(tenantId, patientId, 'referrals') });
      onCreated?.();
    },
  });

  return (
    <FormDialog
      open={open}
      titleKey="med:referral.title"
      submitKey={done ? 'med:common.close' : 'med:referral.submit'}
      screenId={COMP_REFERRAL_EDITOR}
      onSubmit={() => (done ? onClose() : valid ? save.mutate() : undefined)}
      onClose={onClose}
      isDirty={!done && reason.trim() !== ''}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      {done ? (
        <Stack spacing={2}>
          <Alert severity="success" data-testid="med-referral-created">
            {t('med:referral.created')}
          </Alert>
          {alsoNational && canNational && encounterId ? (
            <NationalReferralPanel encounterId={encounterId} patientId={patientId} defaultKind="Horizontal" />
          ) : null}
        </Stack>
      ) : (
        <>
          <Stack direction="row" spacing={1}>
            <TextField select fullWidth label={t('med:referral.target_kind')} value={kind} onChange={(event) => setKind(event.target.value as MedReferralTargetKind)} slotProps={{ htmlInput: { 'data-testid': 'med-referral-kind' } }}>
              {KINDS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`med:target_kind.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField select fullWidth label={t('med:referral.urgency')} value={urgency} onChange={(event) => setUrgency(event.target.value as MedReferralUrgency)} slotProps={{ htmlInput: { 'data-testid': 'med-referral-urgency' } }}>
              {URGENCIES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`med:priority.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {kind === 'Provider' && (
            <TextField select required label={t('med:referral.provider')} value={providerId} onChange={(event) => setProviderId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'med-referral-provider' } }}>
              {(targets.data?.items ?? []).map((item) => (
                <MenuItem key={item.providerId} value={item.providerId}>
                  {item.name} · {t(`med:provider_type.${item.type}`, { defaultValue: item.type })}
                </MenuItem>
              ))}
            </TextField>
          )}
          {kind === 'Specialty' && (
            <TextField select required label={t('med:referral.specialty')} value={specialty} onChange={(event) => setSpecialty(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'med-referral-specialty' } }}>
              {specialties.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`med:specialty.${value}`, { defaultValue: value })}
                </MenuItem>
              ))}
            </TextField>
          )}
          {kind === 'Branch' && (
            <TextField select required label={t('med:referral.branch')} value={branchId} onChange={(event) => setBranchId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'med-referral-branch' } }}>
              {branches.map((value) => (
                <MenuItem key={value} value={value}>
                  {branchNames.find((branch) => branch.branchId === value)?.name ?? value}
                </MenuItem>
              ))}
            </TextField>
          )}
          {kind === 'External' && (
            <Stack spacing={1} data-testid="med-referral-external">
              <TextField select label={t('med:referral.external_referrer')} value={referrerId} onChange={(event) => setReferrerId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'med-referral-referrer' } }}>
                <MenuItem value="">{t('med:referral.external_free')}</MenuItem>
                {(referrers.data?.items ?? []).filter((item) => item.isActive).map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                    {item.specialty ? ` · ${item.specialty}` : ''}
                  </MenuItem>
                ))}
              </TextField>
              {!referrerId && (
                <Stack direction="row" spacing={1}>
                  <TextField fullWidth required label={t('med:referral.external_name')} value={externalName} onChange={(event) => setExternalName(event.target.value)} slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'med-referral-external-name' } }} />
                  <TextField fullWidth label={t('med:referral.external_contact')} value={externalContact} onChange={(event) => setExternalContact(event.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }} />
                </Stack>
              )}
            </Stack>
          )}
          <TextField label={t('med:referral.reason')} required value={reason} onChange={(event) => setReason(event.target.value)} slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'med-referral-reason' } }} />
          <TextField label={t('med:referral.question')} value={question} multiline minRows={2} onChange={(event) => setQuestion(event.target.value)} slotProps={{ htmlInput: { maxLength: 1000, 'data-testid': 'med-referral-question' } }} />
          {canNational && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={alsoNational}
                  onChange={(event) => setAlsoNational(event.target.checked)}
                  data-testid="med-referral-also-national"
                />
              }
              label={t('med:referral.also_national')}
            />
          )}
        </>
      )}
    </FormDialog>
  );
}
